import type { ReactElement, SVGAttributes } from 'react';
import { Svg, Wrapper } from './Flag.styles';

export type FlagCountry = 'DE' | 'CH' | 'FR';

type Props = {
  /** ISO country code of the flag to render. */
  country: FlagCountry;
  translation?: {
    /**
     * Accessible label for the flag. When provided the flag is exposed as an
     * image with this label; when omitted the flag is treated as decorative
     * (e.g. when it sits next to a textual currency code).
     */
    title?: string;
    /** Optional label to show next to the flag. */
    label?: string;
  };
} & Pick<SVGAttributes<SVGSVGElement>, 'className'>;

const flags: Record<FlagCountry, ReactElement> = {
  // black / red / gold horizontal bands
  DE: (
    <g>
      <rect width="28" height="6" fill="#222" />
      <rect y="6" width="28" height="6" fill="#f90805" />
      <rect y="12" width="28" height="6" fill="#fedc36" />
    </g>
  ),
  // blue / white / red vertical bands
  FR: (
    <g>
      <rect x="4" width="8" height="18" fill="#002395" />
      <rect x="12" width="8" height="18" fill="#fff" />
      <rect x="20" width="8" height="18" fill="#ed2939" />
    </g>
  ),
  // red field with a white cross
  CH: (
    <g>
      <rect x="8" width="20" height="18" fill="#f90805" />
      <rect x="16.5" y="2.5" width="3" height="13" fill="#fff" />
      <rect x="11.5" y="7.5" width="13" height="3" fill="#fff" />
    </g>
  ),
};

/**
 * Small country flag rendered as inline SVG. Multi-colour, so it intentionally
 * does not build on a single-fill icon.
 */
const Flag = ({ country, translation, className }: Props) => {
  const hasTitle = translation?.title !== undefined;
  const hasLabel = translation?.label !== undefined;

  return (
    <Wrapper>
      <Svg
        viewBox="0 0 28 18"
        className={className}
        role={hasTitle ? 'img' : undefined}
        aria-label={translation?.title || undefined}
        aria-hidden={hasTitle ? undefined : true}
        focusable="false"
      >
        {hasTitle && <title>{translation.title}</title>}
        {flags[country]}
      </Svg>
      {hasLabel && <span>{translation.label}</span>}
    </Wrapper>
  );
};

export { Flag };
